import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "./lib/supabaseClient";

export default function Auth() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(null);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text: '' }

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleSocialLogin = async (provider) => {
    setSocialLoading(provider);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: { redirectTo: `${window.location.origin}/dashboard` },
      });
      if (error) throw error;
    } catch (err) {
      showMsg("error", err.message);
      setSocialLoading(null);
    }
  };

  const handleAuth = async () => {
    if (!email || !password) return showMsg("error", "Please enter your email and password.");
    setLoading(true);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email, password,
          options: { emailRedirectTo: `${window.location.origin}/dashboard` },
        });
        if (error) throw error;
        try {
          await fetch("https://uhyjiumpwbrqatbbvtwz.supabase.co/functions/v1/welcome-email", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, name: "" }),
          });
        } catch (e) { console.warn("Welcome email failed:", e); }
        showMsg("success", "Account created! Check your inbox to verify your email.");
        setMode("login");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      }
    } catch (err) {
      showMsg("error", err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!email) return showMsg("error", "Enter your email above first.");
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      showMsg("success", `Password reset link sent to ${email}`);
    } catch (err) {
      showMsg("error", err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "#020617",
      display: "flex",
      fontFamily: "'Inter', 'DM Sans', sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .auth-input {
          width: 100%;
          padding: 14px 16px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 12px;
          color: white;
          font-size: 14px;
          font-family: inherit;
          outline: none;
          transition: border-color 0.2s;
        }
        .auth-input::placeholder { color: #475569; }
        .auth-input:focus { border-color: #ef4444; }
        .auth-btn-primary {
          width: 100%;
          padding: 15px;
          background: #ef4444;
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 13px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 1px;
          cursor: pointer;
          transition: all 0.2s;
          font-family: inherit;
        }
        .auth-btn-primary:hover:not(:disabled) { background: #dc2626; transform: translateY(-1px); }
        .auth-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
        .social-btn {
          flex: 1;
          padding: 13px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 12px;
          color: white;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-family: inherit;
        }
        .social-btn:hover:not(:disabled) { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.2); }
        .social-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .tab-btn {
          flex: 1;
          padding: 10px;
          border: none;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
          font-family: inherit;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .tab-active { background: #ef4444; color: white; }
        .tab-inactive { background: transparent; color: #64748b; }
        .tab-inactive:hover { color: white; }
        a { color: #94a3b8; text-decoration: none; transition: color 0.2s; }
        a:hover { color: white; }
        @media (max-width: 900px) { .auth-split-right { display: none !important; } }
      `}</style>

      {/* LEFT — Form */}
      <div style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: "40px 24px",
        minHeight: "100vh",
      }}>
        <div style={{ width: "100%", maxWidth: 400 }}>

          {/* Logo */}
          <div style={{ marginBottom: 40, textAlign: "center" }}>
            <Link to="/">
              <span style={{ fontSize: 28, fontWeight: 900, fontStyle: "italic", color: "white", letterSpacing: "-1px" }}>
                Digitoly <span style={{ color: "#ef4444" }}>QR</span>
              </span>
            </Link>
          </div>

          {/* Heading */}
          <div style={{ marginBottom: 32, textAlign: "center" }}>
            <h1 style={{ fontSize: 26, fontWeight: 900, color: "white", marginBottom: 8, letterSpacing: "-0.5px" }}>
              {mode === "login" ? "Welcome back" : "Create your account"}
            </h1>
            <p style={{ fontSize: 14, color: "#64748b", lineHeight: 1.6 }}>
              {mode === "login"
                ? "Sign in to manage your QR campaigns"
                : "Start your 7-day free trial — no credit card needed"}
            </p>
          </div>

          {/* Tab toggle */}
          <div style={{ display: "flex", background: "rgba(255,255,255,0.05)", padding: 4, borderRadius: 14, marginBottom: 28, gap: 4 }}>
            <button className={`tab-btn ${mode === "login" ? "tab-active" : "tab-inactive"}`} onClick={() => setMode("login")}>Login</button>
            <button className={`tab-btn ${mode === "signup" ? "tab-active" : "tab-inactive"}`} onClick={() => setMode("signup")}>Sign Up</button>
          </div>

          {/* Message */}
          {message && (
            <div style={{
              padding: "12px 16px",
              borderRadius: 10,
              marginBottom: 20,
              fontSize: 13,
              fontWeight: 600,
              background: message.type === "success" ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
              border: `1px solid ${message.type === "success" ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`,
              color: message.type === "success" ? "#22c55e" : "#ef4444",
            }}>
              {message.text}
            </div>
          )}

          {/* Social buttons */}
          <div style={{ display: "flex", gap: 10, marginBottom: 24 }}>
            <button className="social-btn" onClick={() => handleSocialLogin("google")} disabled={!!socialLoading || loading}>
              {socialLoading === "google" ? "..." : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                  Google
                </>
              )}
            </button>
            <button className="social-btn" onClick={() => handleSocialLogin("facebook")} disabled={!!socialLoading || loading}>
              {socialLoading === "facebook" ? "..." : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  Facebook
                </>
              )}
            </button>
          </div>

          {/* Divider */}
          <div style={{ position: "relative", marginBottom: 24 }}>
            <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }} />
            <span style={{ position: "absolute", top: -9, left: "50%", transform: "translateX(-50%)", background: "#020617", padding: "0 12px", fontSize: 11, color: "#475569", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>or continue with email</span>
          </div>

          {/* Email */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Email</label>
            <input className="auth-input" type="email" placeholder="you@company.com" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handleAuth()} />
          </div>

          {/* Password */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>Password</label>
              {mode === "login" && (
                <button onClick={handleResetPassword} style={{ fontSize: 11, color: "#ef4444", fontWeight: 700, background: "none", border: "none", cursor: "pointer", padding: 0 }}>Forgot password?</button>
              )}
            </div>
            <input className="auth-input" type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handleAuth()} />
          </div>

          {/* Submit */}
          <button className="auth-btn-primary" onClick={handleAuth} disabled={loading}>
            {loading ? "Processing..." : mode === "login" ? "Sign In" : "Create Account — Free"}
          </button>

          {/* Trial badge on signup */}
          {mode === "signup" && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 14, padding: "10px", background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.15)", borderRadius: 10 }}>
              <span style={{ fontSize: 14 }}>🎁</span>
              <span style={{ fontSize: 12, color: "#22c55e", fontWeight: 700 }}>7-day free trial · Full Agency access · No credit card</span>
            </div>
          )}

          {/* Legal */}
          <div style={{ marginTop: 32, paddingTop: 24, borderTop: "1px solid rgba(255,255,255,0.06)", textAlign: "center" }}>
            <div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap", marginBottom: 10 }}>
              {[["About", "/p/about"], ["FAQ", "/p/faq"], ["Contact", "/p/contact"], ["Privacy", "/privacy"], ["Terms", "/terms"]].map(([label, href]) => (
                <a key={href} href={href} style={{ fontSize: 11, color: "#334155", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}
                  onMouseOver={e => e.target.style.color = "#94a3b8"} onMouseOut={e => e.target.style.color = "#334155"}>
                  {label}
                </a>
              ))}
            </div>
            <p style={{ fontSize: 11, color: "#1e293b" }}>© 2026 Digitoly QR · All rights reserved</p>
          </div>
        </div>
      </div>

      {/* RIGHT — Branding panel */}
      <div className="auth-split-right" style={{
        width: 480,
        background: "linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)",
        borderLeft: "1px solid rgba(255,255,255,0.06)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: "60px 48px",
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Background glow */}
        <div style={{ position: "absolute", width: 400, height: 400, background: "#ef4444", borderRadius: "50%", filter: "blur(120px)", opacity: 0.08, top: -100, right: -100, pointerEvents: "none" }} />
        <div style={{ position: "absolute", width: 300, height: 300, background: "#8b5cf6", borderRadius: "50%", filter: "blur(100px)", opacity: 0.1, bottom: -80, left: -60, pointerEvents: "none" }} />

        <div style={{ position: "relative", zIndex: 1 }}>
          {/* Tag */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.25)", padding: "5px 14px", borderRadius: 999, marginBottom: 28 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e", animation: "pulse 2s infinite" }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: "#fca5a5", textTransform: "uppercase", letterSpacing: 1.5 }}>Live Platform</span>
          </div>

          <h2 style={{ fontSize: 32, fontWeight: 900, color: "white", lineHeight: 1.2, marginBottom: 16, letterSpacing: "-0.5px" }}>
            QR codes that<br /><span style={{ color: "#ef4444" }}>work harder</span><br />for your business
          </h2>

          <p style={{ fontSize: 14, color: "#64748b", lineHeight: 1.8, marginBottom: 36 }}>
            8 QR modes, real-time analytics, lead capture, review filtering, UPI payments, and digital menus — in one platform.
          </p>

          {/* Feature list */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 40 }}>
            {[
              ["🎯", "Capture leads from every QR scan"],
              ["⭐", "Filter negative reviews automatically"],
              ["📊", "Track country, city, device & time"],
              ["💳", "Accept UPI payments via QR"],
              ["🍽️", "Build digital menus for restaurants"],
              ["⏰", "Set QR expiry and scan limits"],
            ].map(([icon, text]) => (
              <div key={text} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 18, width: 28, textAlign: "center" }}>{icon}</span>
                <span style={{ fontSize: 13, color: "#94a3b8", fontWeight: 500 }}>{text}</span>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {[["7 Day", "Free Trial"], ["8+", "QR Modes"], ["∞", "Scans Tracked"], ["₹499", "Pro/month"]].map(([val, label]) => (
              <div key={label} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, padding: "14px 16px" }}>
                <p style={{ fontSize: 20, fontWeight: 900, color: "#ef4444", letterSpacing: "-0.5px", fontStyle: "italic" }}>{val}</p>
                <p style={{ fontSize: 11, color: "#475569", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginTop: 2 }}>{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
